<script lang="ts">
	//Helpers
	import { get_local_ip, real_id } from '../../../../../helpers/interface_helpers';
	//Services
	import { localReq, postReq } from '../../../../../services/http';
	import { format } from '../../../../../services/i18n';
	//Stores
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Button, Radio, toast, Tooltip } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import SettingGuide from '../../../SettingGuide.svelte';
	//Utilities
	import { tooltipText } from '../tooltip_text';

	export let device;

	const id = real_id(device.id);
	let settings = readDeviceSettings(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore ?? settings;

	let types = ['on', 'off', 'last', 'switch1', 'switch2'];

	const defaultOptionsLegacy = [
		$format('power_on_default_on', {
			default: 'On: this {device.category} device will turn on, when it has power.',
		}),
		$format('power_on_default_off', {
			default: 'Off: this {device.category} device will turn off, when it has power.',
		}),
		$format('power_on_default_last', {
			default: 'Restore last mode: this {device.category} device will use its latest mode.',
		}),
		$format('power_on_default_switch', {
			values: { n: 1 },
			default:
				'SWITCH 1 - Operates this {device.category} according to the state of the switch 1 connected to the device',
		}),
		$format('power_on_default_switch', {
			values: { n: 2 },
			default:
				'SWITCH 2 - Operates this {device.category} according to the state of the switch 2 connected to the device.',
		}),
	];

	const defaultOptionsNewLayout = [
		{
			title: $format('power_on_default_on_new_layout_title', {
				default: 'Turn on',
			}),
			description: $format('power_on_default_on_new_layout_description', {
				default: 'Automatically turns the device on when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_off_new_layout_title', {
				default: 'Turn off',
			}),
			description: $format('power_on_default_off_new_layout_description', {
				default: 'Keeps the device off when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_last_new_layout_title', {
				default: 'Restore last state',
			}),
			description: $format('power_on_default_last_new_layout_description', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_switch1_new_layout_title', {
				default: 'Match input 1',
			}),
			description: $format('power_on_default_switch1_new_layout_description', {
				default:
					'Operates the device according to the state of the switch 1 connected to the device.',
			}),
		},
		{
			title: $format('power_on_default_switch2_new_layout_title', {
				default: 'Match input 2',
			}),
			description: $format('power_on_default_switch2_new_layout_description', {
				default:
					'Operates the device according to the state of the switch 2 connected to the device.',
			}),
		},
	];

	let selected;
	let cache;

	$: if (settings?.[device.category + 's']?.[device.channel]) {
		const channelSettings = settings[device.category + 's'][device.channel];
		if (cache === undefined) {
			selected = channelSettings.default_state;
			cache = selected;
		}
	}

	$: disabled1 =
		settings?.[device.category + 's']?.[device.channel]?.btn1_type === 'edge' ||
		settings?.[device.category + 's']?.[device.channel]?.btn1_type?.includes('momentary');

	$: disabled2 =
		settings?.[device.category + 's']?.[device.channel]?.btn2_type === 'edge' ||
		settings?.[device.category + 's']?.[device.channel]?.btn2_type?.includes('momentary');

	let loading = false;

	let toolTipMesage = (button_id: number) => {
		return {
			message: 'power_on_default_1l_unavailable_edge_or_momentary_info_toast',
			values: { button_id: button_id },
		};
	};

	let localIp = get_local_ip(id);

	const setDefault = (state) => {
		selected = state;
	};

	const sentRequest = async () => {
		if (cache !== selected) {
			let res;
			loading = true;
			if (localIp) {
				res = await localReq(
					device,
					`${localIp}/settings/${device.category}/${device.channel}`,
					'post',
					{
						default_state: selected,
					},
				);
			} else {
				res = await postReq(`device/${device.category}/settings/default_state`, {
					channel: device.channel,
					state: selected,
					id: id,
				});
			}
			if (res.isok) {
				settings[device.category + 's'][device.channel].default_state = selected;
				toast.open({
					type: 'success',
					title:
						$format('power_on_default_scc_toast') || `Power on was changed to ${selected}.`,
				});
				devices_settings.update_devices_settings(device.id, settings);
				cache = selected;
			} else {
				toast.open({ type: 'danger', title: $format('error_global') });
				selected = cache;
			}
			loading = false;
		}
	};
</script>

<SettingGuide
	text={$format('settings_guide_action_on_power_on', {
		default:
			'Choose what this device does when power comes back after a cut — turn on, stay off, or restore the last state.',
	})}
/>

{#if $showNewLayout}
	{#each types as _type, i}
		{#if _type === 'switch1'}
			<Tooltip placement="top" title={tooltipText(toolTipMesage(1), disabled1)}>
				<Radio
					class="mb-s"
					disabled={disabled1}
					size="m"
					{selected}
					value={_type}
					on:click={() => setDefault(_type)}
					id={`power-on-${_type}`}
					info={defaultOptionsNewLayout[i].description}
				>
					{defaultOptionsNewLayout[i].title}
				</Radio>
			</Tooltip>
		{:else if _type === 'switch2'}
			<Tooltip placement="top" title={tooltipText(toolTipMesage(2), disabled2)}>
				<Radio
					class="mb-s"
					disabled={disabled2}
					size="m"
					{selected}
					value={_type}
					on:click={() => setDefault(_type)}
					id={`power-on-${_type}`}
					info={defaultOptionsNewLayout[i].description}
				>
					{defaultOptionsNewLayout[i].title}
				</Radio>
			</Tooltip>
		{:else}
			<Radio
				class="mb-s"
				size="m"
				{selected}
				value={_type}
				on:click={() => setDefault(_type)}
				id={`power-on-${_type}`}
				info={defaultOptionsNewLayout[i].description}
			>
				{defaultOptionsNewLayout[i].title}
			</Radio>
		{/if}
	{/each}
{:else}
	<Radio size="m" {selected} value={types[0]} on:click={() => setDefault(types[0])} id="radio-1m7iiea"
		>{defaultOptionsLegacy[0]}</Radio
	>
	<Radio size="m" {selected} value={types[1]} on:click={() => setDefault(types[1])} id="radio-s22yoq7"
		>{defaultOptionsLegacy[1]}</Radio
	>
	<Radio size="m" {selected} value={types[2]} on:click={() => setDefault(types[2])} id="radio-5880wvv"
		>{defaultOptionsLegacy[2]}</Radio
	>
	<Tooltip placement="top" title={tooltipText(toolTipMesage(1), disabled1)}>
		<Radio
			size="m"
			disabled={disabled1}
			{selected}
			value={types[3]}
			on:click={() => setDefault(types[3])}
			id="radio-7icgqou">{defaultOptionsLegacy[3]}</Radio
		>
	</Tooltip>
	<Tooltip placement="top" title={tooltipText(toolTipMesage(2), disabled2)}>
		<Radio
			size="m"
			disabled={disabled2}
			{selected}
			value={types[4]}
			on:click={() => setDefault(types[4])}
			id="radio-mdeesdw">{defaultOptionsLegacy[4]}</Radio
		>
	</Tooltip>
{/if}

<Button class={$showNewLayout ? 'mt-m' : 'mt-s'} {loading} size="m" width="full" on:click={sentRequest}>
	{$format('button_save')}
</Button>
